import React from "react";
import type { ProjectAction } from "../contexts/ProjectContext";
import { convertDate } from "../utils/dateTimeUtils";
import { projectTypes } from "../utils/main";
import {
  getCodeOffsetEnabled,
  getDocumentRepositoryEnabled,
  getImdrfEnabled,
  getMedicationsEnabled,
  getProcedureTimelineEnabled,
  getRadiologyImagesEnabled,
} from "./ProjectSetting";

export const EmptyProject: ProjectContextType = {
  type: 1,
  name: "",
  defaultPreIndexDays: 0,
  defaultPostIndexDays: 0,
  devices: [],
  isManualCohort: true,
};

export const projectConfigurationError = {
  endDate: "END_DATE",
  missingCohortData: "MISSING_COHORT_DATA",
  datasourceFailed: "DATASOURCE_FAILED",
  cohortInvalidPatientId: "COHORT_INVALID_PATIENT_ID",
  cohortInvalidIndexEncounter: "COHORT_INVALID_INDEX_ENCOUNTER",
  cohortInvalidIndexDate: "COHORT_INVALID_INDEX_DATE",
  cohortDuplicatePatientId: "COHORT_DUPLICATE_PATIENT_ID",
  cohortInvalidPatientInDataSource: "COHORT_INVALID_PATIENT_IN_DATASOURCE",
};

export function convertProjectContextToProject(
  project: ProjectContextType,
): Project {
  return {
    ...project,
    startDate: project.startDate ? project.startDate.toISOString() : undefined,
    endDate: project.endDate ? project.endDate.toISOString() : undefined,
    createdAt: project.createdAt ? project.createdAt.toISOString() : undefined,
    modifiedAt: project.modifiedAt ? project.modifiedAt.toISOString() : null,
    highlightDate: project.highlightDate
      ? project.highlightDate.toISOString()
      : null,
  };
}

export function convertProjectToProjectContext(
  project: Project,
  dispatch?: React.ActionDispatch<[action: ProjectAction]>,
): ProjectContextType {
  const startDate = convertDate.stringToDate(project.startDate);
  const endDate = convertDate.stringToDate(project.endDate);
  const createdAt = convertDate.stringToDate(project.createdAt);
  const modifiedAt = convertDate.stringToDate(project.modifiedAt);
  const highlightDate = convertDate.stringToDate(project.highlightDate);

  const formattedProject = {
    ...project,
    startDate: startDate ? startDate : undefined,
    endDate: endDate ? endDate : undefined,
    createdAt: createdAt ? createdAt : undefined,
    modifiedAt,
    highlightDate,
    imdrfEnabled: getImdrfEnabled(project.projectSettings),
    documentRepositoryEnabled: getDocumentRepositoryEnabled(
      project.projectSettings,
    ),
    medicationsEnabled: getMedicationsEnabled(project.projectSettings),
    radiologyImagesEnabled: getRadiologyImagesEnabled(project.projectSettings),
    procedureTimelineEnabled: getProcedureTimelineEnabled(
      project.projectSettings,
    ),
    codeOffsetEnabled: getCodeOffsetEnabled(project.projectSettings),
  };

  if (dispatch) {
    dispatch({
      type: "update",
      project: formattedProject,
    });
  }

  return formattedProject;
}

export function isDeviceSectionValid(projectObj: ProjectContextType) {
  if (projectObj.type === null) return false;
  if (projectObj.type === projectTypes.device) {
    if (!projectObj.deviceGmdn && projectObj.devices?.length === 0) {
      return false;
    }
    return projectObj.devices?.length !== 0;
  }
  return true;
}

export function isProjectSectionValid(projectObj: ProjectContextType) {
  let datasourceValid = true;
  if (!projectObj.sourceId) {
    datasourceValid = false;
  }
  return projectObj.name.trim() !== "" && datasourceValid;
}

export function isProtocolSectionValid(
  projectObj: ProjectContextType,
  minDate: Date,
) {
  const startDate = projectObj.startDate;
  const endDate = projectObj.endDate;
  if (
    !startDate ||
    !endDate ||
    !(projectObj.defaultPreIndexDays >= 0) ||
    !(projectObj.defaultPostIndexDays >= 0)
  ) {
    return false;
  }
  if (startDate < minDate) {
    return false;
  }
  return endDate >= startDate;
}

export function projectActivityLogs(logs: ActivityLog[]) {
  const { stringToDate } = convertDate;
  return logs.map((log) => {
    return {
      ...log,
      createdAt: stringToDate(log.createdAt),
    };
  });
}
